import { ModalProps } from '@material-ui/core';
import { useCallback, useEffect, useState } from 'react';
import { merge, ObservableInput, of, Subject } from 'rxjs';
import { delay, mergeScan, takeWhile, tap } from 'rxjs/operators';

enum State {
  // Intial state
  INITIAL = 'initial',
  // Temporary state to ensure user has time to see the close confirmation. After a delay, the state is switched to `CONFIRMATION_SHOWN`
  SHOWING_CONFIRMATION = 'showing_confirmation',
  // Confirmation has been shown, triggering close again will switch state to `CONFIRMED`
  // If close is not triggered within a delay, state is switched back to `INITIAL`
  CONFIRMATION_SHOWN = 'confirmation_shown',
  // Confirmation provided, `onCloseWithConfirmation` is triggered
  CONFIRMED = 'confirmed',
}

export const useOnCloseWithConfirmation = ({
  onClose,
}: {
  onClose: () => void;
}): {
  onCloseWithConfirmation: ModalProps['onClose'];
  shouldShowConfirmation: boolean;
} => {
  const [shouldShowConfirmation, setShouldShowConfirmation] = useState<boolean>(false);
  const [closeEvents] = useState(new Subject<void>());
  const onCloseWithConfirmation = useCallback((): void => {
    closeEvents.next();
  }, [closeEvents]);

  useEffect(() => {
    const closeWithConfirmationStates = closeEvents.pipe(
      mergeScan((state: State): ObservableInput<State> => {
        switch (state) {
          case State.INITIAL: {
            return merge(
              of(State.SHOWING_CONFIRMATION),
              of(State.CONFIRMATION_SHOWN).pipe(delay(300)),
              of(State.INITIAL).pipe(delay(2000))
            );
          }
          case State.SHOWING_CONFIRMATION: {
            // nothing to do, events are ignored in this phase
            return [];
          }
          case State.CONFIRMATION_SHOWN: {
            return of(State.CONFIRMED);
          }
          case State.CONFIRMED: {
            // nothing to do, events are ignored in this phase
            return [];
          }
        }
      }, State.INITIAL)
    );

    const subscription = closeWithConfirmationStates
      .pipe(
        takeWhile((value) => value !== State.CONFIRMED, true),
        tap((state) => {
          setShouldShowConfirmation(state === State.SHOWING_CONFIRMATION || state === State.CONFIRMATION_SHOWN);
          if (state === State.CONFIRMED) {
            onClose();
          }
        })
      )
      .subscribe();

    return (): void => {
      subscription.unsubscribe();
    };
  }, [closeEvents, onClose]);

  return { onCloseWithConfirmation, shouldShowConfirmation };
};
